import { FeatherModule } from 'angular-feather';
import { Subscription } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';

export interface DropdownItem {
  value: string;
  label: string;
  svgUrl?: string;
  iconName?: string;
  route?: string[];
  click?: () => void;
  // Extended properties for custom templates
  data?: Record<string, unknown>; // For passing additional data like company object
}

export interface MenuOption {
  value: string;
  route?: [string, ...string[]] | UrlTree;
}

@Component({
  selector: 'lib-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  imports: [CommonModule, FeatherModule],
})
export class DropdownComponent implements AfterViewInit, OnDestroy {
  readonly items = input<DropdownItem[]>([]);
  readonly triggerElement = input.required<ElementRef | HTMLElement>();
  readonly position = input<'below' | 'above' | 'right' | 'left'>('below');
  readonly selectedValue = input<string>('');

  readonly selectDropdownOption = output<string>();

  @ViewChild('dropdownPanel', { read: TemplateRef }) dropdownPanelTemplate!: TemplateRef<unknown>;
  @ContentChild('customItemTemplate') customItemTemplate?: TemplateRef<{
    $implicit: DropdownItem;
    index: number;
  }>;

  isOpen = false;
  private overlayRef?: OverlayRef;
  private portal?: TemplatePortal;
  private backdropSubscription?: Subscription;

  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);

  isMobile = signal(window.innerWidth <= 576);

  constructor() {
    this.breakpointObserver
      .observe(['(max-width: 576px)'])
      .pipe(takeUntilDestroyed())
      .subscribe((result) => {
        this.isMobile.set(result.matches);
      });
  }

  ngAfterViewInit() {
    const trigger = this.getTriggerElement();
    if (trigger) {
      // Bind our toggle handler to the trigger element
      trigger.addEventListener('click', this.toggle.bind(this));
    } else {
      console.error('Trigger element not found.');
    }
  }

  ngOnDestroy() {
    const trigger = this.getTriggerElement();
    if (trigger) {
      trigger.removeEventListener('click', this.toggle.bind(this));
    }
    // Clean up backdrop subscription
    if (this.backdropSubscription) {
      this.backdropSubscription.unsubscribe();
    }
    // Clean up overlay
    this.close();
  }

  private getTriggerElement(): HTMLElement | null {
    const triggerElement = this.triggerElement();
    if (!triggerElement) return null;
    if (triggerElement instanceof ElementRef) {
      return triggerElement.nativeElement;
    }
    return triggerElement as HTMLElement;
  }

  toggle(event?: Event) {
    if (event && !this.isOpen) {
      event.preventDefault();
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    const trigger = this.getTriggerElement();
    if (!trigger) {
      console.error('Trigger element not found.');
      return;
    }

    if (this.overlayRef) {
      return; // Already open
    }

    // Create overlay config based on position
    const positionStrategy = this.overlay.position().flexibleConnectedTo(trigger);

    switch (this.position()) {
      case 'above':
        positionStrategy.withPositions([
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: -8,
          },
        ]);
        break;
      case 'right':
        positionStrategy.withPositions([
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 8,
          },
        ]);
        break;
      case 'left':
        positionStrategy.withPositions([
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'top',
            offsetX: -8,
          },
        ]);
        break;
      case 'below':
      default:
        positionStrategy.withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 8,
          },
        ]);
        break;
    }

    let overlayConfig: OverlayConfig;

    if (this.isMobile()) {
      // Mobile: fullscreen popup with dark overlay
      overlayConfig = {
        positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
        hasBackdrop: true,
        backdropClass: 'dropdown-mobile-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.block(),
        panelClass: 'dropdown-mobile-panel',
      };
    } else {
      // Desktop: positioned dropdown
      overlayConfig = {
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      };
    }

    this.overlayRef = this.overlay.create(overlayConfig);
    this.portal = new TemplatePortal(this.dropdownPanelTemplate, this.viewContainerRef);

    this.overlayRef.attach(this.portal);
    this.isOpen = true;

    // Handle backdrop clicks
    this.backdropSubscription = this.overlayRef.backdropClick().subscribe(() => {
      this.close();
    });
  }

  close() {
    this.isOpen = false;
    if (this.backdropSubscription) {
      this.backdropSubscription.unsubscribe();
      this.backdropSubscription = undefined;
    }
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
      this.portal = undefined;
    }
  }

  handleItemClick(item: DropdownItem) {
    // Invoke optional per-item click handler first
    try {
      item.click?.();
    } catch (e) {
      console.error('Dropdown item click handler threw an error', e);
    }

    if (item.route) {
      this.router.navigate(item.route);
      // Ensure dropdown closes after route navigation for items with routes
      this.close();
    } else {
      this.handleSelect(item.value);
    }
  }

  handleSelect(value: string) {
    this.selectDropdownOption.emit(value);
    this.close();
  }

  onItemMouseDown(item: DropdownItem, event: MouseEvent): void {
    // Prevent default behavior to avoid losing focus or triggering unintended actions
    event.preventDefault();
  }
}
